import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button,
  CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, MenuItem, Paper, Radio, RadioGroup, Step, StepLabel, Stepper,
  Stack, TextField, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import { useTranslation } from 'react-i18next';
import { useTokens } from 'theme';
import { extractApiError } from 'api/errors';
import { useCreateApiKey, useCreateBiConnection, useRotateBiConnection } from 'api/bi';
import ConnectionInstructions, { OAuthSetup } from './ConnectionInstructions';
import { CONNECTION_OPTIONS, OAUTH_PROGRAMS, getProgram, getProgramsForCredential } from './connectionPrograms';
import { ConnectionUnavailable } from './ConnectionSupport';
import ModelScopeField from './ModelScopeField';
import { createSqlAllowedIpsInput, parseSqlAllowedIps, SqlAllowedIpsField, SqlNetworkStatus } from './SqlNetworkAccess';
import useConnectionSecretGuard from './useConnectionSecretGuard';

export default function ConnectionWizard({
  access, accessError, clientIp = null, endpoints, onRetryAccess, onRetryEndpoints, onExit, onCreated,
  rotationCredential = null, replacementCredential = null, initialProgramId = null,
  replacement = false, liveConnections = [],
}) {
  const { t } = useTranslation();
  const theme = useTokens();
  const sqlMutation = useCreateBiConnection();
  const keyMutation = useCreateApiKey();
  const rotateMutation = useRotateBiConnection();
  const { reset: resetSql } = sqlMutation;
  const { reset: resetKey } = keyMutation;
  const { reset: resetRotate } = rotateMutation;
  const [step, setStep] = useState(initialProgramId ? 1 : 0);
  const [programId, setProgramId] = useState(initialProgramId);
  const [aiGuide, setAiGuide] = useState('mcp-generic');
  const [name, setName] = useState(rotationCredential?.name || (replacementCredential ? t('connections.replacementName', { name: replacementCredential.name }) : '') || (initialProgramId ? getProgram(initialProgramId)?.name || t(getProgram(initialProgramId)?.nameKey) : ''));
  const [days, setDays] = useState(replacementCredential ? 'existing' : '90');
  const [customExpiry, setCustomExpiry] = useState('');
  const [profile, setProfile] = useState(rotationCredential?.grant_profile || 'marts');
  const [scopes, setScopes] = useState(null);
  const [scopeError, setScopeError] = useState(false);
  const [advanced, setAdvanced] = useState(false);
  const [suffix, setSuffix] = useState('');
  const [note, setNote] = useState('');
  const [ips, setIps] = useState(createSqlAllowedIpsInput);
  const [issued, setIssued] = useState(null);
  const [error, setError] = useState(null);
  const [nameError, setNameError] = useState(false);
  const [networkError, setNetworkError] = useState(null);
  const [expiryError, setExpiryError] = useState(false);
  const [leave, setLeave] = useState(null);
  const [leaveBusy, setLeaveBusy] = useState(false);
  const mounted = useRef(false);
  const issuing = useRef(false);
  const program = getProgram(programId);
  const isSql = program?.kind === 'sql';
  const channels = replacementCredential?.channels || program?.channels || [];
  const guideId = programId === 'ai-assistant' ? aiGuide : program?.guideId || programId;
  const requestOnly = Boolean(program?.requestOnly || getProgram(guideId)?.requestOnly);
  const oauth = getProgram(guideId)?.kind === 'oauth';
  // The create endpoint defaults an empty scope list to ['*']; do not widen
  // a legacy deny-all key while promising to preserve its permissions.
  const canPreserveScopes = !replacementCredential || (Array.isArray(replacementCredential.scopes) && replacementCredential.scopes.length > 0);
  const pending = sqlMutation.isPending || keyMutation.isPending || rotateMutation.isPending;
  const secret = issued?.password || issued?.key;
  const requestExit = useCallback((proceed) => {
    if (issuing.current) { setLeaveBusy(true); return; }
    setLeave({ proceed, external: true });
  }, []);
  useConnectionSecretGuard(pending, Boolean(secret), requestExit);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      resetSql(); resetKey(); resetRotate();
    };
  }, [resetSql, resetKey, resetRotate]);

  const exit = (completed = false) => {
    if (pending || issuing.current) { setLeaveBusy(true); return; }
    const proceed = () => {
      setIssued(null);
      resetSql(); resetKey(); resetRotate();
      onExit({ completed, replacement });
    };
    if (secret && !completed) setLeave({ proceed, external: false });
    else proceed();
  };
  const hasEndpoint = isSql || (channels.length > 0 && channels.every((channel) => Boolean(endpoints?.[channel]?.url)));
  const sqlReady = !isSql || Boolean(access?.enabled);
  const issue = async () => {
    if (!program || requestOnly || issuing.current || !sqlReady || !hasEndpoint || !canPreserveScopes) return;
    if (!name.trim()) { setNameError(true); return; }
    if (!isSql && !replacementCredential && !rotationCredential && scopes?.length === 0) { setScopeError(true); setAdvanced(true); return; }
    let expiresAt = null;
    if (days === 'existing') {
      expiresAt = replacementCredential.expires_at || null;
      if (expiresAt && Date.parse(expiresAt) <= Date.now()) { setExpiryError(true); return; }
    } else if (days === 'custom') {
      const date = new Date(customExpiry);
      if (!Number.isFinite(date.getTime()) || date.getTime() <= Date.now()) { setExpiryError(true); return; }
      expiresAt = date.toISOString();
    } else if (days !== 'never') expiresAt = new Date(Date.now() + Number(days) * 86400000).toISOString();
    setError(null); setNetworkError(null); setLeaveBusy(false);
    issuing.current = true;
    try {
      let result;
      if (rotationCredential) result = await rotateMutation.mutateAsync(rotationCredential.id);
      else if (isSql) result = await sqlMutation.mutateAsync({
        name: name.trim(), access_type: program.accessType, grant_profile: profile,
        expires_at: expiresAt, username_suffix: suffix.trim(), egress_note: note.trim(),
        ...(access?.network_enabled ? { sql_allowed_ips: parseSqlAllowedIps(ips) } : {}),
      });
      else result = await keyMutation.mutateAsync({
        name: name.trim(), channels,
        scopes: replacementCredential ? replacementCredential.scopes : scopes ?? ['*'],
        expires_at: expiresAt,
      });
      if (!mounted.current) return;
      setIssued(result); setStep(2);
      // Only identifiers reach the parent; the one-time response stays here.
      onCreated?.({ kind: isSql ? 'sql' : 'key', id: result.id, programId: guideId });
    } catch (err) {
      if (!mounted.current) return;
      if (err.code === 'invalid' || err.code === 'limit') setNetworkError(t(err.code === 'limit' ? 'data.sqlAllowedIpsLimit' : 'data.sqlAllowedIpsInvalid'));
      else if (err.response?.data?.sql_allowed_ips) setNetworkError(t('data.sqlAllowedIpsInvalid'));
      else setError(extractApiError(err, t(rotationCredential ? 'connections.rotateError' : 'connections.createError')));
    } finally {
      issuing.current = false;
      // Do not retain success responses or Axios errors in the mutation cache.
      resetSql(); resetKey(); resetRotate();
    }
  };
  const programOptions = (developer) => (
    <RadioGroup value={programId || ''} onChange={(event) => {
      const option = getProgram(event.target.value);
      setProgramId(option.id); setName(option.name || t(option.nameKey)); setError(null);
    }} aria-label={t('connections.program')}>
      {CONNECTION_OPTIONS.filter((item) => Boolean(item.developer) === developer).map((item) => (
        <Paper key={item.id} variant="outlined" sx={{ mb: 1.25, borderColor: programId === item.id ? theme.borderStrong : theme.borderField, bgcolor: programId === item.id ? theme.highlight : theme.surface }}>
          <FormControlLabel value={item.id} control={<Radio />} sx={{ width: '100%', m: 0, px: 1.5, py: 1.25, gap: 1 }} label={(
            <Box><Typography fontWeight={700}>{item.name || t(item.nameKey)}</Typography><Typography variant="body2" color="text.secondary">{t(item.descriptionKey)}</Typography></Box>
          )} />
        </Paper>
      ))}
    </RadioGroup>
  );
  return (
    <Box sx={{
      maxWidth: 760, mx: 'auto',
      // Keep labels above a continuous outline, including empty/select fields.
      // The original MUI label associations and focus/error styles stay intact.
      '& .MuiTextField-root': {
        '& .MuiInputLabel-root': {
          position: 'static', transform: 'none', maxWidth: 'none', mb: 1,
          fontSize: '0.8125rem', lineHeight: 1.5, whiteSpace: 'normal', pointerEvents: 'auto',
        },
        '& .MuiOutlinedInput-input': { fontSize: '0.875rem' },
        '& .MuiOutlinedInput-input:not(textarea)': { py: 1.5 },
        '& .MuiOutlinedInput-notchedOutline': { top: 0, '& legend': { display: 'none' } },
        '& .MuiFormHelperText-root': { mx: 0, mt: 0.75 },
      },
    }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => step === 1 && !rotationCredential && !replacementCredential ? setStep(0) : exit()} disabled={pending} sx={{ mb: 2.5 }}>
        {t(step === 1 && !rotationCredential && !replacementCredential ? 'connections.back' : 'connections.backToList')}
      </Button>
      <Stepper activeStep={step} sx={{ mb: 3 }}>
        {['program', 'access', 'setup'].map((label) => <Step key={label}><StepLabel>{t(`connections.${label}`)}</StepLabel></Step>)}
      </Stepper>
      <Paper sx={{ p: { xs: 2.5, sm: 3.5 } }}>
        <Typography variant="overline" color="text.secondary">{t('connections.step', { current: step + 1 })}</Typography>
        <Typography variant="h5" component="h1" sx={{ mt: 0.75 }}>{t(step === 0 ? 'connections.chooseTitle' : step === 1 ? requestOnly || oauth ? 'connections.instructions' : 'connections.prepareTitle' : 'connections.setupTitle')}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 3 }}>{t(step === 0 ? 'connections.chooseBody' : step === 1 ? oauth ? 'connections.oauthBody' : requestOnly ? 'connections.requestAccessBody' : 'connections.prepareBody' : 'connections.setupBody')}</Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {leaveBusy && <Alert severity="info" sx={{ mb: 2 }}>{t('connections.busyLeave')}</Alert>}
        {step === 0 && <>
          {programOptions(false)}
          <Accordion disableGutters elevation={0} sx={{ mt: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>{t('connections.developers')}</AccordionSummary>
            <AccordionDetails>{programOptions(true)}</AccordionDetails>
          </Accordion>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', borderTop: `1px solid ${theme.border}`, mt: 3, pt: 3 }}>
            <Button variant="contained" endIcon={<ArrowForwardIcon />} disabled={!program} onClick={() => setStep(1)}>{t('connections.continue')}</Button>
          </Box>
        </>}
        {step === 1 && <>
          {programId === 'ai-assistant' && <TextField select fullWidth label={t('connectionGuide.aiClient')} value={aiGuide} onChange={(event) => setAiGuide(event.target.value)} sx={{ mb: 3 }}>
            <MenuItem value="mcp-generic">{t('connectionGuide.generic.mcp')}</MenuItem>
            {OAUTH_PROGRAMS.map((item) => <MenuItem key={item.id} value={item.id}>{t(item.nameKey)}</MenuItem>)}
            {getProgramsForCredential('key', { channels: ['mcp'] }).map((item) => <MenuItem key={item.id} value={item.id}>{item.name || t(item.nameKey)}</MenuItem>)}
          </TextField>}
          {oauth ? <>
            <OAuthSetup programId={guideId} />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', borderTop: `1px solid ${theme.border}`, mt: 3, pt: 3 }}><Button variant="contained" onClick={() => onExit({ completed: false, replacement, oauth: true })}>{t('connections.oauthBackToList')}</Button></Box>
          </> : requestOnly ? <ConnectionInstructions kind={program.kind} credential={{ channels }} programId={guideId} endpoints={endpoints} showGuideSelector={program.kind === 'cube-sql'} /> : <>
          {rotationCredential && <Alert severity="warning" sx={{ mb: 2 }}>{t('connections.rotateWarning')}</Alert>}
          <TextField autoFocus fullWidth label={t('connections.name')} value={name} onChange={(event) => { setName(event.target.value); setNameError(false); }} disabled={pending || Boolean(rotationCredential)} error={nameError} helperText={nameError ? t('connections.nameRequired') : undefined} inputProps={{ maxLength: 255 }} />
          <Alert icon={<ShieldOutlinedIcon />} severity={isSql && profile === 'marts' ? 'success' : 'info'} sx={{ my: 2.5 }}>
            <Typography variant="body2" fontWeight={700}>{t(isSql ? 'connections.readOnlyTitle' : 'connections.keyAccessTitle')}</Typography>
            <Typography variant="body2">{t(isSql ? profile === 'marts' ? 'connections.readOnlyBody' : 'connections.readAllWarning' : replacementCredential ? 'connections.replacementAccess' : 'connections.keyAccessBody')}</Typography>
          </Alert>
          {expiryError && <Alert severity="error" sx={{ mb: 2 }}>{t('connections.expiryInvalid')}</Alert>}
          {!canPreserveScopes && <Alert severity="warning" sx={{ mb: 2 }}>{t('connections.replacementScopesUnavailable')}</Alert>}
          {!rotationCredential && <Accordion expanded={advanced} onChange={(event, open) => setAdvanced(open)} disableGutters elevation={0} sx={{
            border: 0, boxShadow: 'none', bgcolor: theme.raised, borderRadius: 1.5,
            '&.Mui-expanded': { boxShadow: 'none' },
          }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2.5, '&.Mui-expanded': { borderBottom: 0 } }}>{t('connections.advanced')}</AccordionSummary>
            <AccordionDetails sx={{ px: 2.5, pt: 1, pb: 2.5 }}>
              <Stack spacing={2.5}>
              <TextField select fullWidth label={t('connections.expiry')} value={days} onChange={(event) => { setDays(event.target.value); setExpiryError(false); }} disabled={pending}>
                {replacementCredential && <MenuItem value="existing">{t('connections.keepExpiry')}</MenuItem>}
                {[30, 90, 365].map((value) => <MenuItem key={value} value={String(value)}>{t('connections.expiryDays', { days: value })}</MenuItem>)}
                <MenuItem value="never">{t('connections.never')}</MenuItem><MenuItem value="custom">{t('connections.customExpiry')}</MenuItem>
              </TextField>
              {days === 'custom' && <TextField fullWidth type="datetime-local" label={t('connections.expiryDate')} InputLabelProps={{ shrink: true }} value={customExpiry} onChange={(event) => { setCustomExpiry(event.target.value); setExpiryError(false); }} error={expiryError} helperText={expiryError ? t('connections.expiryInvalid') : undefined} />}
              {isSql ? <>
                <TextField select fullWidth label={t('connections.grantProfile')} value={profile} onChange={(event) => setProfile(event.target.value)} disabled={pending}>
                  <MenuItem value="marts">{t('connections.marts')}</MenuItem><MenuItem value="read_all">{t('connections.readAll')}</MenuItem>
                </TextField>
                {profile === 'read_all' && <Alert severity="warning">{t('connections.readAllWarning')}</Alert>}
                <TextField fullWidth label={t('data.usernameSuffixOptional')} value={suffix} onChange={(event) => setSuffix(event.target.value)} disabled={pending} />
                <TextField fullWidth label={t('data.sqlConnectionNoteOptional')} helperText={t('data.sqlConnectionNoteHelp')} value={note} onChange={(event) => setNote(event.target.value)} disabled={pending} />
              </> : !replacementCredential && <ModelScopeField enabled={advanced} value={scopes} onChange={(next) => { setScopes(next); setScopeError(false); }} error={scopeError} disabled={pending} />}
              </Stack>
            </AccordionDetails>
          </Accordion>}
          {isSql && access?.network_enabled && !rotationCredential && <SqlAllowedIpsField clientIp={clientIp} value={ips} onChange={(next) => { setIps(next); setNetworkError(null); }} disabled={pending} error={networkError} />}
          {isSql && <Alert severity="info" sx={{ mt: 2 }}>{t('connections.sqlPreparation')}{' '}{!access?.network_enabled && t('connections.sqlLegacyPreparation')}</Alert>}
          {isSql && !access && <Alert severity={accessError ? 'warning' : 'info'} sx={{ mt: 2 }} action={accessError ? <Button onClick={onRetryAccess}>{t('connections.retry')}</Button> : null}>{t(accessError ? 'connections.accessError' : 'connections.accessLoading')}</Alert>}
          {isSql && access && !access.enabled && <ConnectionUnavailable type="directSql" sx={{ mt: 2 }} />}
          {!hasEndpoint && <Alert severity="warning" sx={{ mt: 2 }} action={<Button onClick={onRetryEndpoints}>{t('connections.retry')}</Button>}>{t('connections.endpointUnavailable')}</Alert>}
          {programId === 'claude-desktop' && <Alert severity="info" sx={{ mt: 2 }}>{t('connections.claudePrerequisite')}</Alert>}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, borderTop: `1px solid ${theme.border}`, mt: 3, pt: 3 }}>
            <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 260 }}>{t('connections.nextSecret')}</Typography>
            <Button variant="contained" onClick={issue} disabled={pending || !sqlReady || !hasEndpoint || !canPreserveScopes} startIcon={pending ? <CircularProgress size={16} color="inherit" /> : null} endIcon={!pending ? <ArrowForwardIcon /> : null}>{t(pending ? 'connections.pending' : rotationCredential ? 'connections.getNewPassword' : 'connections.getSettings')}</Button>
          </Box>
          </>}
        </>}
        {step === 2 && issued && <>
          <Alert severity="warning" sx={{ mb: 3 }}><Typography variant="body2" fontWeight={700}>{t(isSql ? 'connections.passwordTitle' : 'connections.keyTitle')}</Typography>{t('connections.showOnce')}</Alert>
          <ConnectionInstructions kind={isSql ? 'sql' : 'key'} credential={issued} programId={guideId} secret={secret} endpoints={endpoints} />
          {isSql && access?.network_enabled && <Box sx={{ mt: 2 }}><SqlNetworkStatus connection={liveConnections.find((row) => row.id === issued.id) || issued} /></Box>}
          <Alert severity="info" sx={{ mt: 2 }}>{t('connections.manualCheck')}</Alert>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', borderTop: `1px solid ${theme.border}`, mt: 2, pt: 3 }}><Button variant="contained" onClick={() => exit(true)}>{t('connections.finish')}</Button></Box>
        </>}
      </Paper>
      <Dialog open={Boolean(leave)} onClose={() => setLeave(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('connections.unsavedTitle')}</DialogTitle><DialogContent>{t('connections.unsavedBody')}</DialogContent>
        <DialogActions><Button onClick={() => setLeave(null)}>{t('connections.stay')}</Button><Button color="warning" onClick={() => { const chosen = leave; setLeave(null); setIssued(null); resetSql(); resetKey(); resetRotate(); if (chosen?.external) onExit({ completed: false, replacement }); chosen?.proceed(); }}>{t('connections.discard')}</Button></DialogActions>
      </Dialog>
    </Box>
  );
}
